(function () {
  const CONTAINER_SELECTOR = "[data-cart-drawer-provider]";
  const DISPLAY_SELECTOR = "[data-cart-drawer-provider-display]";

  function readCookie(name) {
    if (window.DTCCookieManager?.getCookie) {
      return window.DTCCookieManager.getCookie(name);
    }

    const namePrefix = name + "=";
    const cookie = document.cookie
      .split(";")
      .map((value) => value.trim())
      .find((value) => value.indexOf(namePrefix) === 0);

    return cookie ? cookie.slice(namePrefix.length) : null;
  }

  function normalizeId(value) {
    return value === undefined || value === null ? "" : String(value).trim();
  }

  function isMeaningful(value) {
    if (value === undefined || value === null) return false;

    const normalized = String(value).trim().toLowerCase();
    return (
      normalized !== "" &&
      normalized !== "false" &&
      normalized !== "null" &&
      normalized !== "undefined"
    );
  }

  function escapeHtml(value) {
    return String(value || "").replace(/[&<>"']/g, (character) => {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[character];
    });
  }

  function richTextMarkup(value) {
    const rawValue = String(value || "").trim();
    if (!rawValue) return "";

    const template = document.createElement("template");
    template.innerHTML = rawValue;

    if (!template.content.querySelector("*")) {
      return escapeHtml(rawValue);
    }

    const allowedTags = new Set(["P", "BR", "STRONG", "B", "EM", "I", "A", "H3"]);
    const walker = document.createTreeWalker(
      template.content,
      NodeFilter.SHOW_ELEMENT,
      null
    );
    const disallowed = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;

      if (!allowedTags.has(node.tagName)) {
        disallowed.push(node);
        continue;
      }

      [...node.attributes].forEach((attribute) => {
        if (node.tagName !== "A" || attribute.name !== "href") {
          node.removeAttribute(attribute.name);
        }
      });

      if (node.tagName === "A") {
        const href = node.getAttribute("href") || "";
        if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href)) {
          node.removeAttribute("href");
        }
      }
    }

    disallowed.forEach((node) => {
      node.replaceWith(...node.childNodes);
    });

    return template.innerHTML.trim();
  }

  function splitProviderPromptMarkup(markup) {
    const template = document.createElement("template");
    template.innerHTML = markup;

    const paragraphs = [...template.content.querySelectorAll("p")];
    if (!paragraphs.length) {
      return {
        intro: markup,
        footnote: "",
        zipPrompt: "",
        referral: "",
      };
    }

    const zipParagraph =
      paragraphs.find((paragraph) => /zip/i.test(paragraph.textContent || "")) ||
      paragraphs[2];
    const referralCandidate =
      paragraphs.find((paragraph) =>
        /\breferred\b|practice locator/i.test(paragraph.textContent || "")
      ) || paragraphs[3];
    const referralParagraph =
      referralCandidate && referralCandidate !== zipParagraph ? referralCandidate : null;

    if (zipParagraph) zipParagraph.remove();
    if (referralParagraph) referralParagraph.remove();

    // Whatever copy isn't a path becomes the lead line + a quiet footnote:
    // first remaining paragraph leads, the rest sink to the footnote.
    const remaining = paragraphs.filter(
      (paragraph) => paragraph !== zipParagraph && paragraph !== referralParagraph
    );

    return {
      intro: remaining.length ? remaining[0].outerHTML : template.innerHTML.trim(),
      footnote: remaining.slice(1).map((paragraph) => paragraph.outerHTML).join(""),
      zipPrompt: zipParagraph ? paragraphToPromptMarkup(zipParagraph) : "",
      referral: referralParagraph ? paragraphToPromptMarkup(referralParagraph) : "",
    };
  }

  function paragraphToPromptMarkup(paragraph) {
    const text = (paragraph.textContent || "").trim();
    const questionMatch = text.match(/^(.+?\?)(?:\s+)(.+)$/);

    if (!questionMatch) {
      return paragraph.outerHTML;
    }

    return `<h3>${escapeHtml(questionMatch[1])}</h3><p>${escapeHtml(questionMatch[2])}</p>`;
  }

  function getCartUrl(container) {
    return container?.dataset.cartUrl || window.routes?.cart_url || "/cart";
  }

  function getSelectedProvider() {
    const selectedLocationId = normalizeId(readCookie("location_id"));
    if (!selectedLocationId) return null;

    try {
      const rawProvider = localStorage.getItem("provider_data");
      const provider = rawProvider ? JSON.parse(rawProvider) : null;

      if (provider && normalizeId(provider.id) === selectedLocationId) {
        return provider;
      }
    } catch (error) {
      console.warn("Cart drawer provider: unable to read cached provider data.", error);
    }

    return { id: selectedLocationId };
  }

  function getProviderLinkTabindex(container) {
    return container?.closest?.("#CartDrawer") ? ' tabindex="-1"' : "";
  }

  function getProviderCopy(container) {
    return {
      label: container?.dataset.selectorLabel || "Select Your Preferred Practice",
      requiredText:
        container?.dataset.requiredText ||
        "Select the practice you prefer for consultations and we&rsquo;ll ship the products directly to you.",
      optionalText:
        container?.dataset.optionalText ||
        "Select the practice you prefer for consultations and we&rsquo;ll ship the products directly to you.",
      cta: container?.dataset.selectorCta || "Practice Locator",
    };
  }

  let zipFieldCounter = 0;

  function cleanZip(value) {
    return value.replace(/[^0-9]/g, "").slice(0, 5);
  }

  // ZIP auto-match only renders on the cart page, where its engine is loaded.
  function shouldShowZipMatch(container) {
    if (!container || (container.closest && container.closest("#CartDrawer"))) {
      return false;
    }
    return !!document.querySelector("[data-provider-info]");
  }

  function zipFormMarkup() {
    const fieldId = `cart-provider-zip-${(zipFieldCounter += 1)}`;

    return `
      <form class="cart-drawer-provider__zip" data-cart-drawer-zip-form novalidate>
        <label class="cart-drawer-provider__zip-label visually-hidden" for="${fieldId}">
          Find a practice by ZIP code
        </label>
        <div class="cart-drawer-provider__zip-row">
          <input
            id="${fieldId}"
            class="cart-drawer-provider__zip-input"
            type="text"
            aria-label="ZIP code"
            inputmode="numeric"
            autocomplete="postal-code"
            maxlength="5"
            placeholder="Enter your ZIP code"
            data-cart-drawer-zip-input
          >
          <button type="submit" class="cart-drawer-provider__zip-submit" data-cart-drawer-zip-submit>
            MATCH ME
          </button>
        </div>
        <p class="cart-drawer-provider__zip-feedback" data-cart-drawer-zip-feedback role="status" aria-live="polite" hidden></p>
      </form>
    `;
  }

  function browseLinkMarkup(cartUrl, linkTabindex) {
    return `<a href="${escapeHtml(cartUrl)}#provider-locator" class="cart-drawer-provider__browse" data-cart-drawer-provider-browse${linkTabindex}>Browse all practices<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></a>`;
  }

  const PRACTICE_LOCATOR_PATTERN = /Practice Locator/i;

  function linkPracticeLocatorMarkup(markup, cartUrl, linkTabindex) {
    if (!markup) return browseLinkMarkup(cartUrl, linkTabindex);

    const template = document.createElement("template");
    template.innerHTML = markup;

    if (template.content.querySelector("a")) {
      return template.innerHTML.trim();
    }

    const walker = document.createTreeWalker(
      template.content,
      NodeFilter.SHOW_TEXT,
      null
    );

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const match = node.nodeValue.match(PRACTICE_LOCATOR_PATTERN);
      if (!match) continue;

      const index = match.index;
      const label = match[0];
      const before = node.nodeValue.slice(0, index);
      const after = node.nodeValue.slice(index + label.length);
      const link = document.createElement("a");
      link.href = `${cartUrl}#provider-locator`;
      link.className = "cart-drawer-provider__browse";
      link.setAttribute("data-cart-drawer-provider-browse", "");
      if (linkTabindex) {
        link.setAttribute("tabindex", "-1");
      }
      link.textContent = label;

      node.replaceWith(before, link, after);
      return template.innerHTML.trim();
    }

    return `${template.innerHTML.trim()} ${browseLinkMarkup(cartUrl, linkTabindex)}`;
  }

  function promptMarkup(cartUrl, container) {
    const isRequired =
      document.body?.dataset.requireProviderBeforeCheckout === "true";
    const copy = getProviderCopy(container);
    const promptText = isRequired ? copy.requiredText : copy.optionalText;
    const linkTabindex = getProviderLinkTabindex(container);
    const withZip = shouldShowZipMatch(container);

    const text = richTextMarkup(promptText);
    const textParts = withZip
      ? splitProviderPromptMarkup(text)
      : { intro: text, footnote: "", zipPrompt: "", referral: "" };

    const action = withZip
      ? zipFormMarkup()
      : `<a href="${escapeHtml(cartUrl)}#provider-locator" class="cart-drawer-provider__link" data-cart-drawer-provider-browse${linkTabindex}>${escapeHtml(copy.cta)}</a>`;

    return `
      <div class="cart-drawer-provider__prompt${withZip ? " cart-drawer-provider__prompt--zip" : ""}">
        <div class="cart-drawer-provider__copy">
          <span class="cart-drawer-provider__label">${escapeHtml(copy.label)}</span>
          ${textParts.intro ? `<div class="cart-drawer-provider__text">${textParts.intro}</div>` : ""}
        </div>
        ${withZip ? `
          <div class="cart-drawer-provider__zip-group">
            ${textParts.zipPrompt ? `<div class="cart-drawer-provider__zip-copy">${textParts.zipPrompt}</div>` : ""}
            ${action}
          </div>
          <div class="cart-drawer-provider__locator">
            ${linkPracticeLocatorMarkup(textParts.referral, cartUrl, linkTabindex)}
          </div>
          ${textParts.footnote ? `<div class="cart-drawer-provider__footnote">${textParts.footnote}</div>` : ""}
        ` : action}
      </div>
    `;
  }

  function setZipFeedback(feedback, message, type) {
    if (!feedback) return;
    feedback.textContent = message || "";
    feedback.hidden = !message;
    feedback.classList.toggle("is-error", type === "error");
    feedback.classList.toggle("is-success", type === "success");
  }

  function setZipLoading(submit, isLoading) {
    if (!submit) return;
    if (!submit.dataset.defaultLabel) {
      submit.dataset.defaultLabel = submit.textContent.trim();
    }

    if (isLoading) {
      // Lock width so the label swap doesn't resize the button/input.
      submit.style.minWidth = `${submit.offsetWidth}px`;
      submit.disabled = true;
      submit.textContent = "Matching…";
    } else {
      submit.disabled = false;
      submit.textContent = submit.dataset.defaultLabel;
      submit.style.minWidth = "";
    }
  }

  let mapsWarmed = false;
  function warmUpMaps() {
    if (mapsWarmed) return;
    mapsWarmed = true;
    getLocator()
      .then((locator) => {
        if (locator && typeof locator.warmUp === "function") locator.warmUp();
      })
      .catch(() => {});
  }

  function getLocator() {
    if (window.XtresseProviderLocator) {
      return Promise.resolve(window.XtresseProviderLocator);
    }

    return new Promise((resolve, reject) => {
      const onReady = () => {
        clearTimeout(timer);
        resolve(window.XtresseProviderLocator);
      };
      window.addEventListener("xtresse:provider-locator-ready", onReady, {
        once: true,
      });

      const timer = setTimeout(() => {
        window.removeEventListener("xtresse:provider-locator-ready", onReady);
        if (window.XtresseProviderLocator) resolve(window.XtresseProviderLocator);
        else reject(new Error("Provider matching is unavailable right now."));
      }, 6000);
    });
  }

  async function handleZipSubmit(event, refs) {
    event.preventDefault();

    const { input, submit, feedback } = refs;
    if (!input) return;

    const zip = cleanZip(input.value);
    input.value = zip;

    if (zip.length !== 5) {
      setZipFeedback(feedback, "Enter a valid 5-digit ZIP code.", "error");
      input.focus();
      return;
    }

    setZipFeedback(feedback, "");
    setZipLoading(submit, true);

    try {
      const locator = await getLocator();
      const provider = await locator.matchByZip(zip);
      setZipFeedback(
        feedback,
        provider && provider.location_name
          ? `Matched you with ${provider.location_name}.`
          : "Practice matched.",
        "success"
      );
    } catch (error) {
      console.error("Cart provider ZIP match failed:", error);
      const message = error && error.message ? error.message : "";
      // Keep ZIP validation messages; mask technical failures behind a retry.
      const isUserFacing = /zip code/i.test(message);
      setZipFeedback(
        feedback,
        isUserFacing
          ? message
          : "We're having trouble matching right now. Please try again in a moment.",
        "error"
      );
    } finally {
      setZipLoading(submit, false);
    }
  }

  function bindZipForm(container) {
    const form = container.querySelector("[data-cart-drawer-zip-form]");
    if (!form || form.dataset.bound === "true") return;
    form.dataset.bound = "true";

    const input = form.querySelector("[data-cart-drawer-zip-input]");
    const submit = form.querySelector("[data-cart-drawer-zip-submit]");
    const feedback = form.querySelector("[data-cart-drawer-zip-feedback]");

    if (input) {
      // Warm up Maps on first focus so geocoding is ready at submit.
      input.addEventListener("focus", warmUpMaps, { once: true });

      input.addEventListener("input", () => {
        const cleaned = cleanZip(input.value);
        if (input.value !== cleaned) input.value = cleaned;
        if (feedback && !feedback.hidden) setZipFeedback(feedback, "");
      });
    }

    form.addEventListener("submit", (event) =>
      handleZipSubmit(event, { input, submit, feedback })
    );
  }

  function selectedMarkup(provider, cartUrl, container) {
    const name = isMeaningful(provider?.location_name)
      ? escapeHtml(provider.location_name)
      : "Practice selected";
    const address = isMeaningful(provider?.address)
      ? `<span class="cart-drawer-provider__address">${escapeHtml(provider.address)}</span>`
      : `<span class="cart-drawer-provider__address">Your preferred practice is selected for this order.</span>`;
    const linkTabindex = getProviderLinkTabindex(container);

    return `
      <div class="cart-drawer-provider__selected">
        <div class="cart-drawer-provider__details">
          <span class="cart-drawer-provider__name">${name}</span>
          ${address}
        </div>
        <a href="${escapeHtml(cartUrl)}#provider-locator" class="cart-drawer-provider__link" data-cart-drawer-provider-browse${linkTabindex}>
          Change
        </a>
      </div>
    `;
  }

  function hydrate(container) {
    const display = container.querySelector(DISPLAY_SELECTOR);
    if (!display) return;

    const provider = getSelectedProvider();
    const hasProvider = !!provider;
    const signature = hasProvider
      ? `selected:${provider.id}:${provider.location_name || ""}:${provider.address || ""}`
      : "empty";

    if (container.dataset.providerState === signature) return;

    container.dataset.providerState = signature;
    container.setAttribute("data-has-provider", hasProvider ? "true" : "false");
    if (!hasProvider && container.dataset.hideEmptyProviderPrompt === "true") {
      display.innerHTML = "";
      return;
    }

    display.innerHTML = hasProvider
      ? selectedMarkup(provider, getCartUrl(container), container)
      : promptMarkup(getCartUrl(container), container);

    if (!hasProvider) {
      bindZipForm(container);
    }
  }

  function hydrateAll() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(hydrate);
  }

  function init() {
    hydrateAll();

    window.addEventListener("providerSelected", hydrateAll);
    window.addEventListener("dtcCookieSet", (event) => {
      if (!event.detail || event.detail.name === "location_id") {
        hydrateAll();
      }
    });

    const drawer = document.getElementById("CartDrawer") || document.body;
    const observer = new MutationObserver(hydrateAll);
    observer.observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
